import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { createChart, CandlestickSeries, LineSeries, LineStyle, CrosshairMode } from 'lightweight-charts';
import useDraggable from '../hooks/useDraggable';
import useTrackPageView from '../hooks/useTrackPageView';
import { useTranslation } from '../i18n/LanguageContext';
import { useTheme } from '../hooks/useTheme';
import { useUserSettings } from '../hooks/useUserSettings';

const API_URL = '/api';

// lightweight-charts CSS değişkenlerini anlamaz — bkz. Chart.jsx'teki aynı desen. Gösterge renkleri
// marka/anlam tutarlılığı için her iki temada da sabit kalır.
function getChartColors(theme) {
  const isDark = theme === 'dark';
  return {
    bg:     isDark ? '#1a2028' : '#ffffff',
    text:   isDark ? '#e2e8f0' : '#1e293b',
    grid:   isDark ? '#232a33' : '#f1f5f9',
    border: isDark ? '#313a46' : '#e2e8f0',
    up: '#0d9488', down: '#dc2626', ma50: '#ef4444', ma200: '#3b82f6',
    hma: '#7c3aed', rsi: isDark ? '#93c5fd' : '#172554', rsiMa: '#0d9488', adx: '#f59e0b',
  };
}

const getIntervalLabels = (t) => ({
  '1m': t('backtest.interval1m'), '5m': t('backtest.interval5m'), '15m': t('backtest.interval15m'),
  '30m': t('backtest.interval30m'), '1H': '1h', '4H': '4h', '1D': t('tradeChartModal.interval1D'),
});

// Strateji adları teknik/marka isimleri sayılır — her iki dilde de aynı kalır (BacktestPanel.jsx ile
// tutarlı), yalnızca "Manuel" çevrilir. NOT: rsi50 platformdan KALDIRILDI (yeni seçilemez) ama bu
// dosyadaki tüm rsi50 referansları (bu label, exit-reason eşlemeleri, buildNarrative dalları)
// BİLİNÇLİ OLARAK KORUNUYOR — geçmişte açılmış rsi50 işlemleri hâlâ burada görüntülenebilsin diye.
const getStrategyLabels = (t) => ({
  hma_adx: 'HMA+ADX', range_hma_adx: 'Range Bar+HMA+ADX',
  rsi50: 'RSI-50 Seviyesi', rsi_rsima: 'RSI+SMA',
  trend_pullback_r: 'Trend-Pullback+R', rsima50: 'SMA-50 Seviyesi',
  manual: t('tradeChartModal.strategyManual'),
});

// trade.config, bot başlatılırken kilitlenen TÜM ayarları (dört stratejinin ortak alan kümesini)
// içerir — ama her strateji bunların sadece bir kısmını GERÇEKTEN okur (bkz. backend/analyzer.js +
// trading.js). Bağlam metninde ilgisiz alanları (ör. rsima50 işleminde hma_period/rsi_trigger)
// göstermemek için, her stratejinin fiilen kullandığı alanlarla sınırlanır.
// max_daily_loss_pct/chop_period/chop_threshold/min_rr BİLİNÇLİ OLARAK burada YOK — kodda hâlâ
// okunsalar da Ayarlar ekranının HİÇBİR yerinde kullanıcıya gösterilmiyorlar (rsi50 kaldırılırken
// UI'dan çıkarıldı, bkz. [[project_rsi50_strategy_removed]]) — yani hiçbir stratejiyi gerçekten
// "bağlayan" bir kullanıcı parametresi değiller, Bağlam metninde göstermek kafa karıştırır.
const STRATEGY_CONFIG_FIELDS = {
  range_hma_adx:    ['hma_period', 'adx_period', 'adx_threshold', 'range_box_atr_mult', 'atr_period', 'atr_sl_mult', 'sl_cooldown_min'],
  trend_pullback_r: ['rsi_trigger', 'pullback_atr_mult', 'adx_period', 'adx_threshold', 'atr_period', 'atr_sl_mult', 'sl_cooldown_min'],
  // rsi_rsima/rsima50: ATR yedek-sigorta/cooldown YOK (pctOnly) — bkz. trading.js:PCT_ONLY_STRATEGIES.
  // Ayarlar > Risk'te bu 2 strateji için gösterilen TEK 3 alan (SL%, TP%, RSI Ölü Bölge) ile birebir.
  rsi_rsima:        ['rsi_deadzone', 'tp_pct'],
  rsima50:          ['rsi_deadzone', 'tp_pct'],
};
// sl_pct/market TÜM stratejilerde ortak (Ayarlar > Risk'te de evrensel gösteriliyor).
const UNIVERSAL_CONFIG_FIELDS = ['sl_pct', 'market', 'is_test'];

// config'i, BU işlemin stratejisi/piyasası için anlamlı alanlarla sınırlar — margin/leverage sadece
// vadelide, quantity sadece spot'ta anlamlıdır (aksi halde ör. vadeli işlemde "quantity=null" gibi
// kafa karıştırıcı alakasız alanlar görünür). Tanınmayan/eski bir strateji (ör. kaldırılmış rsi50)
// için sadece evrensel alanlar gösterilir — hatalı değil, eksik gösterim güvenli tarafta kalır.
function relevantConfigEntries(config, strategy) {
  if (!config) return [];
  const allow = new Set([
    ...UNIVERSAL_CONFIG_FIELDS,
    ...(STRATEGY_CONFIG_FIELDS[strategy] || []),
    ...(config.market === 'spot' ? ['quantity'] : ['margin', 'leverage']),
  ]);
  return Object.entries(config).filter(([k]) => allow.has(k));
}

function fmtPrice(price) {
  if (price == null) return '';
  const a = Math.abs(price);
  if (a >= 10000) return price.toFixed(1);
  if (a >= 1000)  return price.toFixed(2);
  if (a >= 10)    return price.toFixed(3);
  if (a >= 1)     return price.toFixed(4);
  return price.toFixed(6);
}

// ms: HAM UTC epoch milisaniye (ör. new Date(trade.created_at).getTime()) — kullanıcının Ayarlar >
// Genel'den seçtiği UTC ofsetine göre gösterilir. Tarayıcının kendi yerel saat dilimine güvenmek
// yerine manuel kaydırma + timeZone:'UTC' formatlama kullanılır (fmtChartTime'daki AYNI desen) —
// böylece ofset kullanıcının seçtiği ARBİTRER değer olabilir (isimli bir IANA bölgesine denk gelmesi
// gerekmez, örn. yarım saatlik dilimler).
function fmtDateTime(ms, offsetMinutes) {
  if (ms == null) return '—';
  return new Date(ms + offsetMinutes * 60000).toLocaleString('tr-TR', {
    timeZone: 'UTC', day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

function fmtNum(v, d = 1) { return v == null ? '—' : v.toFixed(d); }

// İşaretli (+/-) fiyat/yüzde biçimlendirme — mum yeşilse (+), kırmızıysa (-). Chart.jsx'teki
// (canlı ana grafik) aynı desen.
function fmtPriceSigned(v) { return v == null ? '—' : `${v >= 0 ? '+' : '-'}${fmtPrice(Math.abs(v))}`; }
function fmtPctSigned(v, d = 2) { return v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(d)}%`; }

// Mum istatistikleri: A/K = Açılış/Kapanış farkı (mum gövdesi), Maks/Min = High-Low aralığı
// (doğal olarak işaretsiz olduğu için mum rengine göre +/- işareti ZORLA uygulanır).
function candleStats(o, h, l, c) {
  if (o == null || h == null || l == null || c == null) return null;
  const green    = c >= o;
  const sign     = green ? 1 : -1;
  const akVal    = c - o;
  const akPct    = o !== 0 ? (akVal / o) * 100 : null;
  const rangeVal = Math.abs(h - l);
  const rangePct = l !== 0 ? (rangeVal / l) * 100 : null;
  return { green, akVal, akPct, mmVal: sign * rangeVal, mmPct: rangePct != null ? sign * rangePct : null };
}

// sec: lightweight-charts'a verilen ZATEN KAYDIRILMIŞ (backend'in toLocal() ile ürettiği) zaman —
// fmtDateTime'ın aksine burada timeZone:'UTC' kullanılır, yoksa saat bir daha kayar (bkz. fmtDateTime).
function fmtChartTime(sec) {
  if (sec == null) return '—';
  return new Date(sec * 1000).toLocaleString('tr-TR', {
    timeZone: 'UTC', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

// Zaten zamana göre artan sırada olan {time,...} dizisinde time<=t olan elemanları ikili aramayla keser.
function _sliceUpTo(arr, t) {
  if (!arr?.length) return [];
  let lo = 0, hi = arr.length;
  while (lo < hi) { const mid = (lo + hi) >> 1; if (arr[mid].time <= t) lo = mid + 1; else hi = mid; }
  return arr.slice(0, lo);
}

// t anında (ya da ondan önceki en son bilinen) gösterge değeri — anlatım metninde "giriş/çıkış
// anında X şuydu" gibi cümleler için.
function _valueAt(arr, t) {
  if (t == null) return null;
  const s = _sliceUpTo(arr, t);
  return s.length ? s[s.length - 1].value : null;
}

function fmtDuration(ms, t) {
  if (ms == null || ms < 0) return '—';
  const totalMin = Math.round(ms / 60000);
  const days  = Math.floor(totalMin / 1440);
  const hours = Math.floor((totalMin % 1440) / 60);
  const mins  = totalMin % 60;
  const parts = [];
  if (days)  parts.push(t('tradeChartModal.days', { n: days }));
  if (hours) parts.push(t('tradeChartModal.hoursShort', { n: hours }));
  if (mins || parts.length === 0) parts.push(t('tradeChartModal.minutesShort2', { n: mins }));
  return parts.join(' ');
}

function getExitReasonText(t) {
  return {
    sl_hit: () => t('tradeChartModal.exitReasonSlHit'),
    trailing_sl_hit: () => t('tradeChartModal.exitReasonTrailingSl'),
    tp_hit: () => t('tradeChartModal.exitReasonTpHit'),
    trend_reversal: (strategy, side) => strategy === 'trend_pullback_r'
      ? t('tradeChartModal.exitReasonTrendReversalPullback')
      : t('tradeChartModal.exitReasonTrendReversalHma', { side: side === 'long' ? 'SHORT' : 'LONG' }),
    rsi50_deadzone_exit: () => t('tradeChartModal.exitReasonRsi50Deadzone'),
    rsi50_extreme_exit: () => t('tradeChartModal.exitReasonRsi50Extreme'),
    rsi_rsima_exit: () => t('tradeChartModal.exitReasonRsimaExit'),
    rsima50_deadzone_exit: () => t('tradeChartModal.exitReasonRsima50Deadzone'),
    backtest_end: () => t('tradeChartModal.exitReasonBacktestEnd'),
    sl_hit_external: () => t('tradeChartModal.exitReasonSlExternal'),
    tp_hit_external: () => t('tradeChartModal.exitReasonTpExternal'),
    bot_stopped: () => t('tradeChartModal.exitReasonBotStopped'),
    manual: () => t('tradeChartModal.exitReasonManual'),
  };
}

function describeExitReason(reason, strategy, side, t) {
  const fn = getExitReasonText(t)[reason];
  return fn ? fn(strategy, side) : null;
}

// Bir işlemin "hayat döngüsü" — bağlam (parite/periyot/strateji/parametreler), giriş gerekçesi,
// pozisyon süresince yaşananlar ve çıkış gerekçesi — grafiğin altındaki anlatım bloğu için.
function buildNarrative(trade, data, t, offsetMinutes) {
  if (!data?.trade) return null;
  const td = data.trade;
  const entryTime = td.entry?.time, exitTime = td.exit?.time ?? null;

  const entryMa50  = _valueAt(data.ma50,  entryTime), entryMa200 = _valueAt(data.ma200, entryTime);
  const entryRsi   = _valueAt(data.rsi,   entryTime), entryHma   = _valueAt(data.hma,   entryTime);
  const entryRsiMa = _valueAt(data.rsiMa, entryTime);
  const entryAdx   = _valueAt(data.adx,   entryTime);

  const sideLabel   = trade.side === 'long' ? t('tradeChartModal.sideLong') : t('tradeChartModal.sideShort');
  const durationMs  = trade.exitTs && trade.entryTs ? trade.exitTs - trade.entryTs : null;
  const strategyLbl = getStrategyLabels(t)[trade.strategy] || trade.strategy || '—';
  const intervalLbl = getIntervalLabels(t)[trade.interval] || trade.interval || '—';

  const steps = [];

  // 1) Bağlam
  let configText;
  const relevantEntries = relevantConfigEntries(trade.config, trade.strategy);
  if (relevantEntries.length > 0) {
    configText = t('tradeChartModal.configUsed', { list: relevantEntries.map(([k, v]) => `${k}=${v}`).join(', ') });
  } else if (trade.config) {
    configText = t('tradeChartModal.configDefault');
  } else {
    configText = t('tradeChartModal.configUnknown');
  }
  steps.push({
    label: t('tradeChartModal.narrativeContext'),
    text: t('tradeChartModal.contextText', { symbol: trade.symbol, interval: intervalLbl, strategy: strategyLbl, config: configText }),
  });

  // 2) Giriş Anı
  let entryText = t('tradeChartModal.entryBase', { time: fmtDateTime(trade.entryTs, offsetMinutes), price: fmtPrice(trade.entryPrice), side: sideLabel });
  if (trade.strategy === 'trend_pullback_r') {
    const trendState = entryMa50 != null && entryMa200 != null
      ? (entryMa50 > entryMa200 ? t('tradeChartModal.entryTrendUp') : t('tradeChartModal.entryTrendDown'))
      : null;
    entryText += trendState
      ? t('tradeChartModal.entryTrendPullback', { trend: trendState, ma50: fmtPrice(entryMa50), ma200: fmtPrice(entryMa200), rsi: fmtNum(entryRsi) })
      : t('tradeChartModal.entryTrendPullbackFallback');
  } else if (trade.strategy === 'hma_adx' || trade.strategy === 'range_hma_adx') {
    entryText += t('tradeChartModal.entryHmaAdx', { adxNote: entryAdx != null ? t('tradeChartModal.entryHmaAdxNote', { adx: fmtNum(entryAdx) }) : '' });
  } else if (trade.strategy === 'rsi50') {
    const cp = td.entry?.checkpoint;
    entryText += cp
      ? t('tradeChartModal.entryRsi50Cp', { rsi: fmtNum(cp.rsi) })
      : t('tradeChartModal.entryRsi50Fallback');
  } else if (trade.strategy === 'rsi_rsima') {
    const cp = td.entry?.checkpoint;
    entryText += cp
      ? t('tradeChartModal.entryRsimaCp', { rsi: fmtNum(cp.rsi), rsiMa: fmtNum(cp.rsiMa), cmp: trade.side === 'long' ? t('tradeChartModal.entryRsimaCpLong') : t('tradeChartModal.entryRsimaCpShort') })
      : t('tradeChartModal.entryRsimaFallback');
  } else if (trade.strategy === 'rsima50') {
    entryText += entryRsiMa != null
      ? t('tradeChartModal.entryRsima50', { rsiMa: fmtNum(entryRsiMa), cmp: trade.side === 'long' ? t('tradeChartModal.entryRsima50Long') : t('tradeChartModal.entryRsima50Short') })
      : t('tradeChartModal.entryRsima50Fallback');
  }
  steps.push({ label: t('tradeChartModal.narrativeEntry'), text: entryText });

  // 3) Pozisyon Süreci
  let processText;
  const durationStr = fmtDuration(durationMs, t);
  if (!trade.exitTs) {
    processText = t('tradeChartModal.processStillOpen');
  } else if (trade.strategy === 'trend_pullback_r') {
    // trailing_sl_hit: eski (chandelier trailing kaldırılmadan önceki) işlemler için hâlâ
    // oluşabilir geriye dönük kayıtlarda — genel sl_hit metniyle aynı işlem görür, ayrı bir
    // anlatım dalı gerekmiyor artık (yeni işlemlerde SL hiç trailing yapmıyor).
    if (trade.exitReason === 'sl_hit' || trade.exitReason === 'trailing_sl_hit') {
      processText = t('tradeChartModal.processSlHit', { duration: durationStr });
    } else if (trade.exitReason === 'trend_reversal') {
      processText = t('tradeChartModal.processTrendReversal', { duration: durationStr });
    } else {
      processText = t('tradeChartModal.processGeneric', { duration: durationStr });
    }
  } else if (trade.strategy === 'hma_adx' || trade.strategy === 'range_hma_adx') {
    processText = trade.exitReason === 'trend_reversal'
      ? t('tradeChartModal.processHmaReversal', { duration: durationStr })
      : t('tradeChartModal.processHmaSl', { duration: durationStr });
  } else if (trade.strategy === 'rsi50') {
    // rsi50_extreme_exit: eski (aşırı bölge trailing kaldırılmadan önceki) işlemler için hâlâ
    // oluşabilir geriye dönük kayıtlarda — genel metinle aynı işlem görür (yeni işlemlerde bu
    // sebep hiç üretilmiyor).
    processText = t('tradeChartModal.processRsi50Generic', { duration: durationStr });
  } else if (trade.strategy === 'rsi_rsima') {
    processText = t('tradeChartModal.processRsimaGeneric', { duration: durationStr });
  } else if (trade.strategy === 'rsima50') {
    processText = t('tradeChartModal.processRsima50Generic', { duration: durationStr });
  } else {
    processText = t('tradeChartModal.processGeneric', { duration: durationStr });
  }
  steps.push({ label: t('tradeChartModal.narrativeProcess'), text: processText });

  // 4) Çıkış
  if (trade.exitTs) {
    const reasonText = describeExitReason(trade.exitReason, trade.strategy, trade.side, t);
    const pnlVal = trade.pnl != null ? parseFloat(trade.pnl) : null;
    let exitText = t('tradeChartModal.exitBase', { time: fmtDateTime(trade.exitTs, offsetMinutes), price: fmtPrice(trade.exitPrice) });
    if (reasonText) exitText += t('tradeChartModal.exitReasonSuffix', { reason: reasonText });
    if (pnlVal != null) exitText += t('tradeChartModal.exitResultSuffix', { result: pnlVal >= 0 ? t('tradeChartModal.resultProfit') : t('tradeChartModal.resultLoss'), pnl: `${pnlVal >= 0 ? '+' : ''}${pnlVal.toFixed(2)}` });
    steps.push({ label: t('tradeChartModal.narrativeExit'), text: exitText });
  }

  return steps;
}

// [fromIdx,toIdx] aralığını, gerekirse minCount'a ulaşana kadar ÖNCE geçmişe (daha bol veri var),
// yetmezse ileri doğru genişletir — hem replay/zaman çizelgesi hem görsel zoom için ortak kullanılır.
function _expandRange(fromIdx, toIdx, minCount, maxIdx) {
  let f = fromIdx, t = toIdx;
  const deficit = minCount - (t - f + 1);
  if (deficit > 0) {
    const backExtra = Math.min(deficit, f);
    f -= backExtra;
    const stillNeeded = deficit - backExtra;
    if (stillNeeded > 0) t = Math.min(maxIdx, t + stillNeeded);
  }
  return [f, t];
}

const REPLAY_LEAD = 6; // başlangıç - 6 mum (işlevsel replay/timeline sınırı — görsel barSpacing sınırından bağımsız)
const REPLAY_TAIL = 6; // bitiş + 6 mum

/**
 * Geçmişte gerçekleşmiş bir işlemi (canlı ya da backtest), gerçekleştiği andaki GERÇEK OKX mum
 * verisi ve stratejinin kullandığı göstergelerle (MA50/MA200 + RSI/RSIMA ya da HMA/ADX) birlikte
 * görselleştirir. AI/OCR yok, yükleme yok — sadece OKX'in kendi geçmiş verisi. "Doğrulama" değil,
 * işlemin nasıl gerçekleştiğini maksimum detayla anlatan bir görsel.
 *
 * trade: { symbol, interval, strategy, side, entryTs, exitTs, entryPrice, exitPrice, slPrice,
 *          tpPrice, pnl, signal }
 */
export default function TradeChartModal({ trade, token, onClose }) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { utcOffsetMinutes } = useUserSettings();
  const themeRef = useRef(theme);
  themeRef.current = theme;
  useTrackPageView('Islem Gorsellestirmesi');
  const containerRef = useRef(null);
  const chartRef      = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [data,    setData]    = useState(null);
  // Fare grafik üzerinde gezerken o anki mumun OHLC + gösterge değerleri — Chart.jsx'teki (canlı
  // ana grafik) aynı crosshair-takip deseni. Grafikten çıkınca null'a döner, en son değere düşülür.
  const [hover, setHover] = useState(null);

  // İşlemin oluşum sürecini mum mum canlandıran "tekrar oynatma" — kullanıcının botun kararını
  // gerçek veri üzerinde adım adım izleyip doğrulayabilmesi için (şüpheyi ortadan kaldırmak amacıyla).
  const seriesRef    = useRef(null); // { candleSeries, ma50Series, ma200Series, rsiSeries, rsiMaSeries, hmaSeries, adxSeries }
  const replayRef     = useRef({ startIdx: 0, endIdx: 0, entryIdx: -1, exitIdx: -1 });
  const replayIdxRef  = useRef(0);
  const playTimerRef  = useRef(null);
  const [isPlaying,     setIsPlaying]    = useState(false);
  const [replayIdx,     setReplayIdx]    = useState(null); // null = tekrar oynatma modunda değil
  const [replayBadge,   setReplayBadge]  = useState(null); // 'entry' | 'exit' | null
  const [replaySpeed,   setReplaySpeed]  = useState(2);
  const [replayBounds,  setReplayBounds] = useState(null); // { startIdx, endIdx, entryIdx, exitIdx } — timeline (scrubber) için

  // Tek bir kareyi (index i'ye kadar birikmiş veri) çizer — hem otomatik oynatma hem elle
  // zaman çizelgesi (scrubber) sürüklemesi bunu kullanır.
  const renderFrame = (i) => {
    const S = seriesRef.current, R = replayRef.current;
    if (!S || !data) return;
    const clamped = Math.max(R.startIdx, Math.min(R.endIdx, i));
    const t = data.candles[clamped].time;
    S.candleSeries.setData(data.candles.slice(0, clamped + 1));
    if (S.ma50Series)  S.ma50Series.setData(_sliceUpTo(data.ma50, t));
    if (S.ma200Series) S.ma200Series.setData(_sliceUpTo(data.ma200, t));
    if (S.rsiSeries)   S.rsiSeries.setData(_sliceUpTo(data.rsi, t));
    if (S.rsiMaSeries) S.rsiMaSeries.setData(_sliceUpTo(data.rsiMa, t));
    if (S.hmaSeries)   S.hmaSeries.setData(_sliceUpTo(data.hma, t));
    if (S.adxSeries)   S.adxSeries.setData(_sliceUpTo(data.adx, t));
    setReplayIdx(clamped);
    setReplayBadge(clamped === R.entryIdx ? 'entry' : clamped === R.exitIdx ? 'exit' : null);
    replayIdxRef.current = clamped;
  };

  const stepReplay = () => {
    const R = replayRef.current;
    if (replayIdxRef.current > R.endIdx) { setIsPlaying(false); playTimerRef.current = null; return; }
    renderFrame(replayIdxRef.current);
    replayIdxRef.current += 1;
    playTimerRef.current = setTimeout(stepReplay, 500 / replaySpeed);
  };

  const handlePlay = () => {
    if (!seriesRef.current || replayRef.current.entryIdx < 0) return;
    if (replayIdxRef.current < replayRef.current.startIdx || replayIdxRef.current > replayRef.current.endIdx) {
      replayIdxRef.current = replayRef.current.startIdx;
    }
    setIsPlaying(true);
    stepReplay();
  };

  const handlePause = () => {
    if (playTimerRef.current) { clearTimeout(playTimerRef.current); playTimerRef.current = null; }
    setIsPlaying(false);
  };

  // Zaman çizelgesini elle sürükleme — oynatmayı duraklatır ve seçilen kareye anında atlar.
  const handleSeek = (idx) => {
    handlePause();
    renderFrame(idx);
  };

  const handleResetReplay = () => {
    handlePause();
    replayIdxRef.current = 0;
    setReplayIdx(null);
    setReplayBadge(null);
    const S = seriesRef.current;
    if (!S || !data) return;
    S.candleSeries.setData(data.candles);
    if (S.ma50Series)  S.ma50Series.setData(data.ma50 || []);
    if (S.ma200Series) S.ma200Series.setData(data.ma200 || []);
    if (S.rsiSeries)   S.rsiSeries.setData(data.rsi || []);
    if (S.rsiMaSeries) S.rsiMaSeries.setData(data.rsiMa || []);
    if (S.hmaSeries)   S.hmaSeries.setData(data.hma || []);
    if (S.adxSeries)   S.adxSeries.setData(data.adx || []);
  };

  useEffect(() => () => { if (playTimerRef.current) clearTimeout(playTimerRef.current); }, []);

  // Modalı başlıktan sürükleyip taşıma.
  const { offset: dragOffset, onMouseDown: handleHeaderMouseDown } = useDraggable();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`${API_URL}/chart/trade-context`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(trade),
    })
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (cancelled) return;
        if (!ok) throw new Error(d.error || t('tradeChartModal.fetchFailed'));
        setData(d);
      })
      .catch(err => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!data?.candles?.length || !containerRef.current) return;
    const C = getChartColors(themeRef.current);

    const chart = createChart(containerRef.current, {
      width: containerRef.current.clientWidth,
      height: data.adx?.length ? 620 : ((data.rsi?.length || data.hma?.length) ? 520 : 420),
      layout: { background: { color: C.bg }, textColor: C.text, fontSize: 10 },
      grid:   { vertLines: { color: C.grid }, horzLines: { color: C.grid } },
      crosshair:       { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: C.border },
      timeScale:       { borderColor: C.border, timeVisible: true, secondsVisible: false },
    });
    chartRef.current = chart;

    const priceFmt = { type: 'price', precision: 6, minMove: 0.000001 };
    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: C.up, downColor: C.down, borderUpColor: C.up, borderDownColor: C.down,
      wickUpColor: C.up, wickDownColor: C.down, priceFormat: priceFmt,
    }, 0);
    candleSeries.setData(data.candles);

    let ma50Series = null, ma200Series = null, rsiSeries = null, rsiMaSeries = null, hmaSeries = null, adxSeries = null;

    if (data.ma50?.length) {
      ma50Series = chart.addSeries(LineSeries, { color: C.ma50, lineWidth: 1, priceLineVisible: false, lastValueVisible: false, priceFormat: priceFmt }, 0);
      ma50Series.setData(data.ma50);
    }
    if (data.ma200?.length) {
      ma200Series = chart.addSeries(LineSeries, { color: C.ma200, lineWidth: 1, priceLineVisible: false, lastValueVisible: false, priceFormat: priceFmt }, 0);
      ma200Series.setData(data.ma200);
    }

    // RSI14/RSIMA — MA50/MA200 gibi stratejiden bağımsız her zaman gösterilen çekirdek göstergeler
    // (alt panel, pane 1). HMA (fiyat ölçekli, pane 0) ve ADX (kendi alt paneli, pane 2) SADECE
    // HMA-bazlı stratejilerde (hma_adx/range_hma_adx) veri içerir — birbirini dışlamaz, aynı anda
    // gösterilebilirler (önceden else-if idi, RSI artık her zaman dolu olduğundan bu HMA/ADX'i hiç
    // göstermezdi).
    if (data.rsi?.length) {
      rsiSeries = chart.addSeries(LineSeries, { color: C.rsi, lineWidth: 1, priceLineVisible: false, lastValueVisible: false }, 1);
      rsiSeries.setData(data.rsi);
      rsiSeries.priceScale().applyOptions({ scaleMargins: { top: 0.1, bottom: 0.1 } });
      // RSI 30/50/70 seviyeleri — RSI için evrensel önemli sınırlar, her zaman gösterilir (bkz. Chart.jsx'teki aynı desen).
      rsiSeries.createPriceLine({ price: 70, color: '#C7C7C7', lineWidth: 1, lineStyle: LineStyle.Solid, axisLabelVisible: false, title: '' });
      rsiSeries.createPriceLine({ price: 50, color: '#777777', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: false, title: '' });
      rsiSeries.createPriceLine({ price: 30, color: '#C7C7C7', lineWidth: 1, lineStyle: LineStyle.Solid, axisLabelVisible: false, title: '' });
      if (data.rsiMa?.length) {
        rsiMaSeries = chart.addSeries(LineSeries, { color: C.rsiMa, lineWidth: 1, priceLineVisible: false, lastValueVisible: false }, 1);
        rsiMaSeries.setData(data.rsiMa);
      }
    }
    if (data.hma?.length) {
      hmaSeries = chart.addSeries(LineSeries, { color: C.hma, lineWidth: 1, priceLineVisible: false, lastValueVisible: false, priceFormat: priceFmt }, 0);
      hmaSeries.setData(data.hma);
      if (data.adx?.length) {
        adxSeries = chart.addSeries(LineSeries, { color: C.adx, lineWidth: 1, priceLineVisible: false, lastValueVisible: false }, 2);
        adxSeries.setData(data.adx);
        adxSeries.priceScale().applyOptions({ scaleMargins: { top: 0.1, bottom: 0.1 } });
      }
    }

    seriesRef.current = { candleSeries, ma50Series, ma200Series, rsiSeries, rsiMaSeries, hmaSeries, adxSeries };

    // Fare grafik üzerinde gezerken o anki mumun OHLC + gösterge değerlerini oku (Chart.jsx'teki
    // canlı ana grafikle aynı davranış).
    chart.subscribeCrosshairMove((param) => {
      if (!param.time || !param.seriesData) { setHover(null); return; }
      const c = param.seriesData.get(candleSeries);
      if (!c) { setHover(null); return; }
      setHover({
        open: c.open, high: c.high, low: c.low, close: c.close,
        ma50: ma50Series ? param.seriesData.get(ma50Series)?.value ?? null : null,
        ma200: ma200Series ? param.seriesData.get(ma200Series)?.value ?? null : null,
        rsi: rsiSeries ? param.seriesData.get(rsiSeries)?.value ?? null : null,
        rsiMa: rsiMaSeries ? param.seriesData.get(rsiMaSeries)?.value ?? null : null,
        hma: hmaSeries ? param.seriesData.get(hmaSeries)?.value ?? null : null,
        adx: adxSeries ? param.seriesData.get(adxSeries)?.value ?? null : null,
      });
    });

    const panes = chart.panes();
    if (panes[0]) panes[0].setHeight((data.rsi?.length || data.hma?.length) ? 340 : 400);
    if (panes[1]) panes[1].setHeight(140);
    if (panes[2]) panes[2].setHeight(140);

    // Giriş/çıkış/SL/TP yatay referans çizgileri — canlı grafikteki (_redrawPriceLines) aynı desen.
    // Mum üzerine bindirilen ok/daire işaretleri (createSeriesMarkers) KASITLI OLARAK yok — orta
    // alanda candle'ları kapatan fazlalık nesnelerdi; çizgiler ise kenar etiketli, gürültü yapmıyor.
    const mkLine = (price, color, label, style = LineStyle.Solid) =>
      candleSeries.createPriceLine({ price, color, lineWidth: 1, lineStyle: style, axisLabelVisible: true, title: `${label} ${fmtPrice(price)}` });
    const td = data.trade || {};
    if (td.entry?.price != null) mkLine(td.entry.price, '#f59e0b', t('tradeChartModal.entryLineLabel'), LineStyle.Dashed);
    if (td.exit?.price  != null) mkLine(td.exit.price,  '#6366f1', t('tradeChartModal.exitLineLabel'), LineStyle.Dotted);
    if (td.slPrice != null) mkLine(td.slPrice, '#ef4444', 'SL');
    if (td.tpPrice) mkLine(td.tpPrice, '#10b981', 'TP'); // 0 = TP tanımlı değil (sadece SL kullanılıyor)

    // İşlevsel replay/zaman çizelgesi sınırı temelde başlangıç-6/bitiş+6 mumdur ama en az
    // MIN_REPLAY_FRAMES kareye (scrubber'ın anlamlı olması için) tamamlanır. GÖRSEL zoom ise
    // bunun üzerine, en az MIN_VISIBLE_CANDLES mum gösterecek şekilde AYRICA genişler (az mumu
    // sabit genişliğe sığdırınca her biri devasa/kalın görünüyordu). Her iki genişleme de önce
    // geçmişe doğru yapılır (220 mumluk bol lookback zaten fetch edilmiş durumda).
    // "↺ Sıfırla" butonu tam aralığı geri getirir.
    const MIN_REPLAY_FRAMES   = 25;
    const MIN_VISIBLE_CANDLES = 100;
    const entryIdx = data.candles.findIndex(c => c.time === td.entry?.time);
    const exitIdx  = td.exit ? data.candles.findIndex(c => c.time === td.exit.time) : entryIdx;
    if (entryIdx >= 0) {
      const maxIdx = data.candles.length - 1;
      const [fromIdx, toIdx] = _expandRange(
        Math.max(0, entryIdx - REPLAY_LEAD),
        Math.min(maxIdx, Math.max(entryIdx, exitIdx) + REPLAY_TAIL),
        MIN_REPLAY_FRAMES, maxIdx,
      );
      replayRef.current = { startIdx: fromIdx, endIdx: toIdx, entryIdx, exitIdx };
      replayIdxRef.current = fromIdx;
      setReplayBounds(replayRef.current);

      const [visFromIdx, visToIdx] = _expandRange(fromIdx, toIdx, MIN_VISIBLE_CANDLES, maxIdx);
      chart.timeScale().setVisibleRange({
        from: data.candles[visFromIdx].time,
        to:   data.candles[visToIdx].time,
      });
    } else {
      replayRef.current = { startIdx: 0, endIdx: 0, entryIdx: -1, exitIdx: -1 };
      setReplayBounds(null);
      chart.timeScale().fitContent();
    }

    const handleResize = () => {
      if (!containerRef.current) return;
      chart.applyOptions({ width: containerRef.current.clientWidth });
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
      chartRef.current = null;
    };
  }, [data]); // eslint-disable-line

  // Tema değişince mevcut grafik yeniden oluşturulmaz — sadece bg/text/grid/border applyOptions
  // ile güncellenir (bkz. Chart.jsx'teki aynı desen).
  useEffect(() => {
    if (!chartRef.current) return;
    const C = getChartColors(theme);
    chartRef.current.applyOptions({
      layout: { background: { color: C.bg }, textColor: C.text },
      grid:   { vertLines: { color: C.grid }, horzLines: { color: C.grid } },
      rightPriceScale: { borderColor: C.border },
      timeScale:       { borderColor: C.border },
    });
  }, [theme]);

  const pnl = trade.pnl != null ? parseFloat(trade.pnl) : null;

  const C = getChartColors(theme);
  const INTERVAL_LABELS = getIntervalLabels(t);
  const STRATEGY_LABELS = getStrategyLabels(t);

  const last = (arr) => (arr?.length ? arr[arr.length - 1].value : null);
  const lastMa50  = last(data?.ma50);
  const lastMa200 = last(data?.ma200);
  const lastRsi   = last(data?.rsi);
  const lastRsiMa = last(data?.rsiMa);
  const lastHma   = last(data?.hma);
  const lastAdx   = last(data?.adx);
  const lastCandle = data?.candles?.length ? data.candles[data.candles.length - 1] : null;
  const narrative = buildNarrative(trade, data, t, utcOffsetMinutes);
  const cs = candleStats(
    hover?.open  ?? lastCandle?.open,
    hover?.high  ?? lastCandle?.high,
    hover?.low   ?? lastCandle?.low,
    hover?.close ?? lastCandle?.close,
  );

  // Portal ile doğrudan document.body'ye render edilir — bu modal Backtest gibi sürüklenebilir
  // (transform'lu) bir üst pencerenin İÇİNDEN açılırsa, o üst elemanın transform'u fixed-position
  // overlay'in "viewport"unu üst elemanın kendi (dar) kutusuna daraltır (CSS spec: transform'lu bir
  // ata, içindeki position:fixed elemanlar için yeni bir containing block oluşturur) — bu da modalın
  // flex-shrink ile olması gerekenden çok daha dar görünmesine yol açardı. Portal bunu kökten çözer.
  return createPortal(
    <div className="log-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="trade-chart-modal" style={{ transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)` }}>
        <div className="verify-modal-header trade-chart-drag-handle" onMouseDown={handleHeaderMouseDown}>
          <div>
            <p className="card-title" style={{ marginBottom: 4 }}>
              {trade.symbol} · {trade.side?.toUpperCase()} — {t('tradeChartModal.visualizationTitle')}
            </p>
            <div className="trade-chart-summary">
              {trade.strategy && <span>{t('tradeChartModal.strategyLabel')} <strong>{STRATEGY_LABELS[trade.strategy] || trade.strategy}</strong></span>}
              {trade.interval && <span>{t('tradeChartModal.periodLabel')} <strong>{INTERVAL_LABELS[trade.interval] || trade.interval}</strong></span>}
              <span>{t('tradeChartModal.entryLabel')} <strong>{fmtDateTime(trade.entryTs, utcOffsetMinutes)}</strong> @ {fmtPrice(trade.entryPrice)}</span>
              {trade.exitTs && <span>{t('tradeChartModal.exitLabel')} <strong>{fmtDateTime(trade.exitTs, utcOffsetMinutes)}</strong> @ {fmtPrice(trade.exitPrice)}</span>}
              {trade.slPrice != null && <span style={{ color: '#ef4444' }}>SL: {fmtPrice(trade.slPrice)}</span>}
              {trade.tpPrice ? <span style={{ color: '#10b981' }}>TP: {fmtPrice(trade.tpPrice)}</span> : null}
              {pnl != null && <span className={pnl >= 0 ? 'pnl-pos' : 'pnl-neg'}>P&amp;L: {pnl >= 0 ? '+' : ''}{pnl.toFixed(2)}</span>}
            </div>
            {(data?.trade?.entry?.checkpoint || data?.trade?.exit?.checkpoint) && (
              <div className="trade-chart-summary" style={{ marginTop: 2 }}>
                {data.trade.entry?.checkpoint && (
                  <span style={{ color: 'var(--muted)' }}>
                    {t('tradeChartModal.decisionRsiEntry')}{' '}
                    <strong>{fmtNum(data.trade.entry.checkpoint.rsi)}</strong> vs SMA <strong>{fmtNum(data.trade.entry.checkpoint.rsiMa)}</strong>
                  </span>
                )}
                {data.trade.exit?.checkpoint && (
                  <span style={{ color: 'var(--muted)' }}>
                    {t('tradeChartModal.decisionRsiExit')}{' '}
                    <strong>{fmtNum(data.trade.exit.checkpoint.rsi)}</strong> vs SMA <strong>{fmtNum(data.trade.exit.checkpoint.rsiMa)}</strong>
                  </span>
                )}
              </div>
            )}
          </div>
          <button className="settings-close" onClick={onClose}>✕</button>
        </div>
        {(data?.trade?.entry?.checkpoint || data?.trade?.exit?.checkpoint) && (
          <p style={{ fontSize: 11, color: 'var(--muted)', margin: '0 0 8px' }}>
            {t('tradeChartModal.checkpointNote')}
          </p>
        )}

        {loading ? (
          <div className="empty-state">{t('tradeChartModal.loading')}</div>
        ) : error ? (
          <div className="msg msg-error">{error}</div>
        ) : !data?.candles?.length ? (
          <div className="empty-state">{t('tradeChartModal.noCandles')}</div>
        ) : (
          <>
            <div className="chart-legend">
              {lastMa50  != null && <span className="legend-item" style={{ color: C.ma50 }}>── MA50 {fmtPrice(lastMa50)}</span>}
              {lastMa200 != null && <span className="legend-item" style={{ color: C.ma200 }}>── MA200 {fmtPrice(lastMa200)}</span>}
              {lastRsi   != null && <span className="legend-item" style={{ color: C.rsi }}>── RSI {lastRsi.toFixed(1)}{trade.strategy === 'rsi50' ? t('chart.rsi50Note') : (trade.strategy === 'rsima50' ? t('tradeChartModal.rsimaRefNote') : '')}</span>}
              {lastRsiMa != null && <span className="legend-item" style={{ color: C.rsiMa }}>── SMA {lastRsiMa.toFixed(1)}{trade.strategy === 'rsima50' ? t('chart.rsi50Note') : t('tradeChartModal.rsimaRefNote')}</span>}
              {lastHma   != null && <span className="legend-item" style={{ color: C.hma }}>── HMA {fmtPrice(lastHma)}</span>}
              {lastAdx   != null && <span className="legend-item" style={{ color: C.adx }}>── ADX {lastAdx.toFixed(1)}</span>}
              <span className="legend-symbol">{trade.symbol}</span>
              <button className="chart-reset-btn" onClick={() => chartRef.current?.timeScale().fitContent()} title={t('tradeChartModal.resetTitle')}>
                {t('chart.resetButton')}
              </button>
            </div>
            <div className="chart-hover-bar">
              <span className="legend-item">O <strong>{fmtPrice(hover?.open  ?? lastCandle?.open)}</strong></span>
              <span className="legend-item">H <strong>{fmtPrice(hover?.high  ?? lastCandle?.high)}</strong></span>
              <span className="legend-item">L <strong>{fmtPrice(hover?.low   ?? lastCandle?.low)}</strong></span>
              <span className="legend-item">C <strong>{fmtPrice(hover?.close ?? lastCandle?.close)}</strong></span>
              {lastMa50  != null && <span className="legend-item" style={{ color: C.ma50 }}>MA50 <strong>{fmtPrice(hover?.ma50 ?? lastMa50)}</strong></span>}
              {lastMa200 != null && <span className="legend-item" style={{ color: C.ma200 }}>MA200 <strong>{fmtPrice(hover?.ma200 ?? lastMa200)}</strong></span>}
              {lastRsi   != null && <span className="legend-item" style={{ color: C.rsi }}>RSI <strong>{fmtNum(hover?.rsi ?? lastRsi)}</strong></span>}
              {lastRsiMa != null && <span className="legend-item" style={{ color: C.rsiMa }}>SMA <strong>{fmtNum(hover?.rsiMa ?? lastRsiMa)}</strong></span>}
              {lastHma   != null && <span className="legend-item" style={{ color: C.hma }}>HMA <strong>{fmtPrice(hover?.hma ?? lastHma)}</strong></span>}
              {lastAdx   != null && <span className="legend-item" style={{ color: C.adx }}>ADX <strong>{fmtNum(hover?.adx ?? lastAdx)}</strong></span>}
              {cs && (
                <>
                  <span className="legend-item" style={{ color: cs.green ? C.up : C.down }}>{t('chart.akPct')} <strong>{fmtPctSigned(cs.akPct)}</strong></span>
                  <span className="legend-item" style={{ color: cs.green ? C.up : C.down }}>{t('chart.akVal')} <strong>{fmtPriceSigned(cs.akVal)}</strong></span>
                  <span className="legend-item" style={{ color: cs.green ? C.up : C.down }}>{t('chart.mmPct')} <strong>{fmtPctSigned(cs.mmPct)}</strong></span>
                  <span className="legend-item" style={{ color: cs.green ? C.up : C.down }}>{t('chart.mmVal')} <strong>{fmtPriceSigned(cs.mmVal)}</strong></span>
                </>
              )}
            </div>
            {replayBounds && (
              <div className="replay-bar">
                {!isPlaying ? (
                  <button className="replay-btn" onClick={handlePlay} title={t('tradeChartModal.play')}>▶</button>
                ) : (
                  <button className="replay-btn" onClick={handlePause} title={t('tradeChartModal.pause')}>⏸</button>
                )}
                <button className="replay-btn-ghost" onClick={handleResetReplay} title={t('tradeChartModal.restart')}>⏹</button>
                <div className="replay-timeline">
                  <input
                    type="range"
                    min={replayBounds.startIdx}
                    max={replayBounds.endIdx}
                    value={replayIdx ?? replayBounds.startIdx}
                    onChange={e => handleSeek(parseInt(e.target.value, 10))}
                  />
                  {replayBounds.entryIdx >= 0 && (
                    <div
                      className="replay-marker"
                      style={{
                        left: `${((replayBounds.entryIdx - replayBounds.startIdx) / Math.max(1, replayBounds.endIdx - replayBounds.startIdx)) * 100}%`,
                        background: '#16a34a',
                      }}
                      title={t('tradeChartModal.entryMoment')}
                    />
                  )}
                  {replayBounds.exitIdx >= 0 && (
                    <div
                      className="replay-marker"
                      style={{
                        left: `${((replayBounds.exitIdx - replayBounds.startIdx) / Math.max(1, replayBounds.endIdx - replayBounds.startIdx)) * 100}%`,
                        background: '#dc2626',
                      }}
                      title={t('tradeChartModal.exitMoment')}
                    />
                  )}
                </div>
                <span className="replay-time">{fmtChartTime(data.candles[replayIdx ?? replayBounds.startIdx]?.time)}</span>
                <select className="replay-speed" value={replaySpeed} onChange={e => setReplaySpeed(Number(e.target.value))} title={t('tradeChartModal.playbackSpeed')}>
                  <option value={1}>1x</option>
                  <option value={2}>2x</option>
                  <option value={4}>4x</option>
                  <option value={8}>8x</option>
                </select>
              </div>
            )}
            <div className="trade-chart-container">
              {/* lightweight-charts bu div'in içeriğini kendi yönetir — React children KOYULMAZ,
                  rozet overlay'i bu yüzden ayrı bir sarmalayıcıda, ayrı bir kardeş olarak durur. */}
              <div ref={containerRef} />
              {replayBadge === 'entry' && <div className="replay-badge replay-badge-entry">{t('tradeChartModal.entryBadge')} {fmtPrice(trade.entryPrice)}</div>}
              {replayBadge === 'exit'  && <div className="replay-badge replay-badge-exit">{t('tradeChartModal.exitBadge')} {fmtPrice(trade.exitPrice)}</div>}
            </div>
            {narrative && (
              <div className="trade-narrative">
                <p className="trade-narrative-title">{t('tradeChartModal.narrativeTitle')}</p>
                {narrative.map((s, i) => (
                  <div className="trade-narrative-step" key={i}>
                    <span className="trade-narrative-label">{s.label}</span>
                    <p className="trade-narrative-text">{s.text}</p>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
